import axios from 'axios'
import { captureError, SENTRY_SEVERITY, toApiError, toServerError } from 'src/service/utils/sentry'
import { CMD_TYPES } from 'src/types/cmdtype'
import { ERR_CODES } from 'src/types/errcode'
import { ResponseHeader } from 'src/types/type'

/** 모니터링 대상 CmdType (전체) */
export const WATCH_CMD_TYPES = new Set<number>(Object.values(CMD_TYPES))

/** 정의된 ErrCode (정상 실패 간주) */
export const KNOWN_ERR_CODES = new Set<number>(Object.values(ERR_CODES))

/**
 * HTTP 200 응답의 알 수 없는 ErrCode를 ApiError로 남기는 함수
 *
 * watch CmdType이 아니거나, ErrCode 목록에 있는 known 실패면 수집하지 않는다.
 * ErrorBoundary / HTTP 5xx와 역할을 섞지 않는다.
 * 서버가 내려준 ErrMsg를 그대로 메시지/extra에 남겨 Issue만 보고도 원인을 알 수 있게 한다.
 */
export function captureUnexpectedErrCode(header: ResponseHeader) {
  const { ErrCode, CmdType, ErrMsg } = header

  if (!CmdType || !WATCH_CMD_TYPES.has(CmdType)) return
  if (!ErrCode || KNOWN_ERR_CODES.has(ErrCode)) return

  captureError(
    toApiError(
      new Error(
        `[API] Unexpected error | cmdType=${cmdTypeLabel(CmdType)} errCode=${ErrCode} ${
          ErrMsg ? ` : ${ErrMsg}` : ''
        }`,
      ),
    ),
    {
      level: SENTRY_SEVERITY.Warning,
      tags: { cmdType: String(CmdType), errCode: String(ErrCode) },
      fingerprint: ['api-unexpected-error', String(CmdType), String(ErrCode)],
    },
  )
}

const HTTP_STATUS_LABEL: Record<number, string> = {
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
}

/**
 * axios HTTP 5xx만 ServerError로 남기는 함수
 *
 * offline / ERR_NETWORK / abort / 4xx는 제외한다.
 */
export function captureServerError(error: unknown, cmdType: number) {
  if (!axios.isAxiosError(error)) return

  const status = error.response?.status
  if (!status || status < 500 || status >= 600) return

  const statusLabel = HTTP_STATUS_LABEL[status] ?? 'Server Error'

  captureError(toServerError(new Error(`[Server] HTTP ${status} ${statusLabel}`)), {
    level: SENTRY_SEVERITY.Error,
    tags: {
      httpStatus: String(status),
      cmdType: String(cmdType),
    },
    extra: {
      method: error.config?.method,
      response: error.response?.data,
    },
    fingerprint: ['server-error', String(cmdType), String(status)],
  })
}

/**
 * CmdType 숫자를 Sentry 표기용 라벨로 변환한다.
 *
 * @example
 * cmdTypeLabel(10013) // "requestSendCertNumber (10013)"
 * cmdTypeLabel(9999)  // "9999"
 */

export function cmdTypeLabel(cmdType: number): string {
  for (const [name, value] of Object.entries(CMD_TYPES)) {
    if (value === cmdType) return `${name} (${cmdType})`
  }
  return String(cmdType)
}
